(() => {
  if (customElements.get('yb-ultra-motion')) return;
  class YbUltraMotion extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.abort = new AbortController();
      this.loop = this.querySelector('[data-motion-loop]');
      this.toggle = this.querySelector('[data-motion-toggle]');
      this.watch = this.querySelector('[data-motion-watch]');
      this.dialog = this.querySelector('[data-motion-dialog]');
      this.film = this.querySelector('[data-motion-film]');
      this.error = this.querySelector('[data-motion-error]');
      this.filmError = this.querySelector('[data-motion-film-error]');
      this.filmRetry = this.querySelector('[data-motion-retry]');
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.inView = false;
      this.userPaused = false;
      this.manualLoop = false;
      this.removeAttribute('data-loop-ready');
      this.updatePaused();
      this.toggle.hidden = false;
      this.loop.muted = true;
      if (this.watch && typeof this.dialog?.showModal === 'function') this.watch.hidden = false;
      const on = (target, name, handler) => target?.addEventListener(name, handler, { signal: this.abort.signal });
      on(this.toggle, 'click', () => {
        if (!this.loop.paused) {
          this.userPaused = true;
          this.loop.pause();
        } else {
          this.userPaused = false;
          this.manualLoop = true;
          this.playLoop();
        }
      });
      on(this.loop, 'playing', () => {
        this.setAttribute('data-loop-ready', '');
        this.setAttribute('data-loop-playing', '');
        this.toggle.setAttribute('aria-label', this.dataset.labelPause);
        this.error.hidden = true;
      });
      on(this.loop, 'pause', () => this.updatePaused());
      on(this.loop, 'error', () => {
        this.userPaused = true;
        this.manualLoop = false;
        this.loop.pause();
        this.loop.removeAttribute('src');
        this.loop.load();
        this.removeAttribute('data-loop-ready');
        this.updatePaused();
        this.error.hidden = false;
      });
      on(this.watch, 'click', () => this.openFilm());
      on(this.querySelector('[data-motion-close]'), 'click', () => this.dialog.close());
      on(this.dialog, 'click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      on(this.dialog, 'close', () => this.closeFilm());
      on(this.film, 'error', () => this.failFilm());
      on(this.filmRetry, 'click', () => this.loadFilm());
      on(document, 'visibilitychange', () => {
        if (document.hidden) {
          this.loop.pause();
          this.film?.pause();
        } else {
          this.autoLoop();
        }
      });
      on(this.motionPreference, 'change', () => this.preferenceChanged());
      on(this.connection, 'change', () => this.preferenceChanged());
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
          if (this.inView) this.autoLoop();
          else this.loop.pause();
        }, { threshold: [0, 0.15] });
        this.observer.observe(this.querySelector('[data-motion-stage]'));
      }
    }
    chooseSource(kind) {
      const mobile = matchMedia('(max-width: 767px)').matches;
      const value = mobile ? this.dataset[`${kind}Mobile`] : this.dataset[`${kind}Desktop`];
      if (!value?.trim()) return '';
      try {
        const url = new URL(value, location.origin);
        return url.protocol === 'https:' ? url.href : '';
      } catch {
        return '';
      }
    }
    constrained() {
      return this.motionPreference.matches || Boolean(this.connection?.saveData);
    }
    updatePaused() {
      this.removeAttribute('data-loop-playing');
      this.toggle.setAttribute('aria-label', this.dataset.labelPlay);
    }
    preferenceChanged() {
      if (this.constrained()) {
        this.manualLoop = false;
        this.loop.pause();
        this.loop.removeAttribute('src');
        this.loop.load();
        this.removeAttribute('data-loop-ready');
      } else this.autoLoop();
    }
    autoLoop() {
      if (!this.inView || document.hidden || this.userPaused || this.dialog?.open) return;
      if (this.manualLoop || (this.dataset.autoplay === 'true' && !this.constrained())) this.playLoop();
    }
    async playLoop() {
      if (document.hidden || this.dialog?.open) return;
      if (!this.loop.getAttribute('src')) {
        const source = this.chooseSource('loop');
        if (!source) return;
        this.loop.src = source;
      }
      try {
        await this.loop.play();
      } catch {
        this.updatePaused();
      }
    }
    openFilm() {
      if (!this.dialog || !this.film || this.dialog.open) return;
      this.loop.pause();
      this.returnFocus = document.activeElement;
      this.dialog.showModal();
      this.film.poster = this.querySelector('[data-motion-poster] img')?.currentSrc || '';
      this.loadFilm();
    }
    loadFilm() {
      this.filmError.hidden = true;
      this.filmRetry.hidden = true;
      const source = this.chooseSource('film');
      if (!source) {
        this.failFilm();
        return;
      }
      this.film.src = source;
      this.film.play().catch(() => {});
    }
    failFilm() {
      this.film.pause();
      this.film.removeAttribute('src');
      this.film.load();
      this.filmError.hidden = false;
      this.filmRetry.hidden = false;
    }
    closeFilm() {
      this.film.pause();
      this.film.removeAttribute('src');
      this.film.load();
      this.filmError.hidden = true;
      this.filmRetry.hidden = true;
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.autoLoop();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.abort?.abort();
      this.loop?.pause();
      this.loop?.removeAttribute('src');
      this.loop?.load();
      if (this.dialog?.open) this.dialog.close();
      this.film?.pause();
      this.film?.removeAttribute('src');
      this.film?.load();
      this.bound = false;
    }
  }
  customElements.define('yb-ultra-motion', YbUltraMotion);
})();
